import { FC, useCallback, useEffect, useState } from 'react';

import { useFetchAllArkivresurserByGenreFormStrikt } from 'hooks/fetchAllArkivresurserByGenreFormStrikt';
import { useDialog } from 'hooks/useDialog';

import { RaaDialog, RaaSpinner } from 'raa-react';

import { DrawerSection, SpinnerContainer } from 'components/drawerSections/DrawerSections';
import FotografiList from 'components/utkast/fotografiList/FotografiList';
import FotografiModal from 'components/utkast/fotografiModal/FotografiModal';
import ImageFileUploadInput, { ValidFileMimeTypesEnum } from 'components/utkast/imageFileUpload/ImageFileUpload';

import {
  BebyggelseobjektUtkast,
  BildResursData,
  GranskningResursIndex,
  SearchIndexResult,
  VinjettPutParam
} from 'models';

import { DokumentationstypEnum, GenreFormStriktEnum } from 'enums';

import { useDeleteDokumentationMutation } from 'api/endpoints/dokumentationSlice';
import { toMellanlagerResurs, toResursDataFromSearchIndexResult } from 'utilities/mappers';
import { isBildResursData } from 'utilities/typeGuards';

interface FotografiSectionProps {
  utkast: BebyggelseobjektUtkast;
  onSelectVinjett: (utkastId: string, vinjettParam: VinjettPutParam) => Promise<void>;
  selectedVinjett?: VinjettPutParam;
  dokumentation: Array<GranskningResursIndex>;
  getDokumentationIsFetching: boolean;
}

const FotografiSection: FC<FotografiSectionProps> = ({
  utkast,
  onSelectVinjett,
  selectedVinjett,
  dokumentation,
  getDokumentationIsFetching
}) => {
  const { setDialog, unsetDialog } = useDialog();
  const [mergedResurser, setMergedResurser] = useState<Array<BildResursData>>([]);
  const [arkivResursdata, setArkivResursdata] = useState<Array<BildResursData>>([]);
  const [allFetchedArkivdata, setAllFetchedArkivdata] = useState<Array<SearchIndexResult>>([]);

  const [deleteDokumentation] = useDeleteDokumentationMutation();
  const { fetchAllArkivresurser } = useFetchAllArkivresurserByGenreFormStrikt(utkast, GenreFormStriktEnum.FOTOGRAFI);

  useEffect(() => {
    const fetchAll = async () => {
      const resurser = await fetchAllArkivresurser();
      setAllFetchedArkivdata(resurser);
    };

    void fetchAll();
  }, [fetchAllArkivresurser]);

  useEffect(() => {
    if (selectedVinjett) {
      setMergedResurser((prevMergedResurser) =>
        prevMergedResurser.map((resurs) => ({
          ...resurs,
          vinjett: selectedVinjett.arkivreferens_uri === resurs.uri || resurs.resursId === selectedVinjett.resurs_id
        }))
      );
    }
  }, [selectedVinjett]);

  useEffect(() => {
    if (allFetchedArkivdata.length) {
      const resursdata = allFetchedArkivdata
        .filter((res) => res.genreFormStrikt === GenreFormStriktEnum.FOTOGRAFI)
        .map((res) => toResursDataFromSearchIndexResult(res, utkast.vinjett?.url))
        .filter(isBildResursData);

      setArkivResursdata(resursdata);
    }
  }, [allFetchedArkivdata, utkast.vinjett?.url]);

  useEffect(() => {
    if (dokumentation.length) {
      setMergedResurser(
        dokumentation.map((d) => toMellanlagerResurs(d) as BildResursData).concat(arkivResursdata || [])
      );
    } else {
      setMergedResurser(arkivResursdata || []);
    }
  }, [dokumentation, arkivResursdata]);

  const deleteResurs = useCallback(
    async (resursId: string) =>
      deleteDokumentation({
        utkastId: utkast.utkast_id,
        resursId,
        isVinjett: dokumentation?.find((d) => d.resurs_id === resursId)?.vinjett || false
      }),
    [deleteDokumentation, dokumentation, utkast.utkast_id]
  );

  return (
    <DrawerSection>
      <fieldset>
        <legend className="tw-heading-md tw-mb-4">
          Fotografier{mergedResurser.length > 0 && ` (${mergedResurser.length} st)`}
        </legend>
        <ImageFileUploadInput
          labelText="Lägg till fotografi"
          name="sectionImageFileUpload"
          dokumentationstyp={DokumentationstypEnum.BILD}
          validFileMimeTypes={[ValidFileMimeTypesEnum.IMAGE_JPEG, ValidFileMimeTypesEnum.IMAGE_PNG]}
          uploading={() => {
            setDialog(RaaDialog, {
              header: 'Laddar upp fil',
              ariaDescribedBy: 'dialog-description',
              children: (
                <div className="tw-flex tw-justify-center tw-py-12">
                  <RaaSpinner size="3rem" />
                  <span id="dialog-description" className="tw-ml-2 tw-self-center">
                    Vänta...
                  </span>
                </div>
              )
            });
          }}
          success={(file: File, height: number, width: number, date?: Date) => {
            unsetDialog();
            setDialog(FotografiModal, {
              titel: utkast.titel,
              file: file,
              utkastId: utkast.utkast_id,
              height: height,
              width: width,
              date: date,
              onSubmit: () => unsetDialog()
            });
          }}
        />
      </fieldset>
      <SpinnerContainer showSpinner={getDokumentationIsFetching} size="4rem">
        {dokumentation && (
          <FotografiList
            dokumentation={dokumentation}
            resurser={mergedResurser}
            deleteResurs={deleteResurs}
            onSelectVinjett={onSelectVinjett}
            utkastId={utkast.utkast_id}
            vinjett={utkast.vinjett || null}
          />
        )}
      </SpinnerContainer>
    </DrawerSection>
  );
};

export default FotografiSection;
